const CALENDAR_PADDING_DAYS = 3;
const MAX_CALENDAR_DAYS = 120;
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

export const getTruckId = (truck) => truck?.id ?? truck?.truck_id;
export const getTruckDate = (truck) => truck?.ship_date || truck?.date || truck?.recommended_ship_date;
export const getTruckRegion = (truck) => truck?.okrug || truck?.region || truck?.region_name || truck?.okrug_name;
export const truckCellKey = (date, region) => `${date || ''}__${region || ''}`;
export const canMoveTruckToRegion = (truck, targetRegion) => Boolean(
  truck
  && targetRegion
  && getTruckRegion(truck)
  && String(getTruckRegion(truck)) === String(targetRegion),
);

export const formatFboNumber = (value) => (
  value == null ? '—' : new Intl.NumberFormat('ru-RU').format(Math.round(Number(value)))
);

export const formatFboFloat = (value) => (
  value == null
    ? '—'
    : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(Number(value))
);

export const formatFboDate = (value) => (
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('ru-RU') : '—'
);

const dateValue = (value) => (typeof value === 'object' && value !== null
  ? value.ship_date || value.date || value.key || value.value
  : value);

const regionValue = (value) => (typeof value === 'object' && value !== null
  ? value.okrug || value.region || value.key || value.value || value.name
  : value);

const regionLabel = (value) => (typeof value === 'object' && value !== null
  ? value.label || value.okrug_short || value.short || regionValue(value)
  : value);

const isYmd = (value) => typeof value === 'string' && YMD_RE.test(value);

const parseMaybeJson = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const formatYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toYmd = (value) => {
  if (!value) return null;
  if (isYmd(value)) return value;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return formatYmd(value);
  if (typeof value === 'string' && isYmd(value.slice(0, 10))) return value.slice(0, 10);
  return null;
};

const parseYmd = (value) => {
  if (!isYmd(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const addDays = (ymd, days) => {
  const date = parseYmd(ymd);
  if (!date) return null;
  date.setDate(date.getDate() + days);
  return formatYmd(date);
};

const daysInclusive = (from, to) => {
  const start = parseYmd(from);
  const end = parseYmd(to);
  if (!start || !end || end < start) return 0;
  return Math.floor((end - start) / 86400000) + 1;
};

const compactDateParts = (value) => {
  const date = parseYmd(value);
  if (!date) return { label: formatFboDate(value), weekday: '' };
  return {
    label: date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
    weekday: date.toLocaleDateString('ru-RU', { weekday: 'short' }).replace('.', ''),
  };
};

const firstYmdByKeys = (sources, keys) => {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    for (const key of keys) {
      if (isYmd(source[key])) return source[key];
    }
  }
  return null;
};

const firstPositiveNumber = (sources, keys) => {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    for (const key of keys) {
      const value = Number(source[key]);
      if (Number.isFinite(value) && value > 0) return Math.floor(value);
    }
  }
  return null;
};

const dateValuesFromSource = (source) => {
  if (!source || typeof source !== 'object') return [];
  const keys = ['dates', 'calendar_dates', 'calendarDates', 'ship_dates', 'shipDates', 'supply_dates', 'supplyDates'];
  return keys.flatMap((key) => (
    Array.isArray(source[key])
      ? source[key].map((item) => toYmd(dateValue(item))).filter(Boolean)
      : []
  ));
};

const enumerateDates = (from, to) => {
  const totalDays = Math.min(daysInclusive(from, to), MAX_CALENDAR_DAYS);
  if (!totalDays) return [];
  return Array.from({ length: totalDays }, (_, index) => addDays(from, index)).filter(Boolean);
};

export function collectUniqueTruckRows(rawPlan, onDuplicate = null) {
  const plan = rawPlan || {};
  let source = [];

  // API может отдавать канонический trucks[] и derived cells[] одновременно.
  // Это альтернативные представления одних данных, складывать их нельзя.
  if (Array.isArray(plan)) {
    source = plan;
  } else if (Array.isArray(plan.trucks) && plan.trucks.length) {
    source = plan.trucks;
  } else if (Array.isArray(plan.items) && plan.items.length) {
    source = plan.items;
  } else if (Array.isArray(plan.cells)) {
    source = plan.cells.flatMap((cell) => {
      const cellDate = dateValue(cell);
      const cellRegion = regionValue(cell);
      return (cell?.trucks || cell?.items || []).map((truck) => ({
        ...truck,
        ship_date: getTruckDate(truck) || cellDate,
        okrug: getTruckRegion(truck) || cellRegion,
      }));
    });
  } else if (plan.grid && typeof plan.grid === 'object') {
    source = Object.entries(plan.grid).flatMap(([date, regions]) => (
      Object.entries(regions || {}).flatMap(([region, value]) => {
        const rows = Array.isArray(value) ? value : (value?.trucks || value?.items || []);
        return rows.map((truck) => ({
          ...truck,
          ship_date: getTruckDate(truck) || date,
          okrug: getTruckRegion(truck) || region,
        }));
      })
    ));
  }

  const seen = new Set();
  return source.filter((truck, index) => {
    const id = getTruckId(truck);
    if (id == null) return true;
    const key = String(id);
    if (!seen.has(key)) {
      seen.add(key);
      return true;
    }
    onDuplicate?.({ id, index });
    return false;
  });
}

const buildCalendarDates = (plan, trucks, rawPlan) => {
  const truckDates = trucks.map(getTruckDate).filter(isYmd).sort();
  const minTruckDate = truckDates[0] || null;
  const maxTruckDate = truckDates[truckDates.length - 1] || null;
  const settings = parseMaybeJson(plan?.settings_json || plan?.settings);
  const filters = parseMaybeJson(plan?.filters_json || plan?.filters);
  const sourcePreview = parseMaybeJson(settings?.source_preview || plan?.source_preview || rawPlan?.source_preview);
  const sourcePlan = parseMaybeJson(sourcePreview?.plan || settings?.plan || plan?.plan || rawPlan?.plan);
  const sources = [plan, settings, filters, sourcePreview, sourcePlan, rawPlan];
  const sourceDates = sources.flatMap(dateValuesFromSource).sort();
  const explicitStart = firstYmdByKeys(sources, ['plan_from', 'planFrom', 'date_from', 'dateFrom', 'start_date', 'startDate', 'from']);
  const explicitEnd = firstYmdByKeys(sources, ['plan_to', 'planTo', 'date_to', 'dateTo', 'end_date', 'endDate', 'to']);
  const horizonDays = firstPositiveNumber(sources, ['horizon_days', 'horizonDays', 'plan_days', 'planDays']);

  let start = null;
  let end = null;
  if (explicitStart && explicitEnd) {
    start = minTruckDate && minTruckDate < explicitStart ? minTruckDate : explicitStart;
    end = maxTruckDate && maxTruckDate > explicitEnd ? maxTruckDate : explicitEnd;
  } else if (sourceDates.length) {
    start = minTruckDate && minTruckDate < sourceDates[0] ? minTruckDate : sourceDates[0];
    end = maxTruckDate && maxTruckDate > sourceDates[sourceDates.length - 1]
      ? maxTruckDate
      : sourceDates[sourceDates.length - 1];
  } else if (horizonDays && minTruckDate) {
    start = minTruckDate;
    end = addDays(minTruckDate, horizonDays - 1);
    if (maxTruckDate && maxTruckDate > end) end = maxTruckDate;
  } else if (minTruckDate && maxTruckDate) {
    start = minTruckDate;
    end = maxTruckDate;
  }

  if (!start || !end) return [];
  start = addDays(start, -CALENDAR_PADDING_DAYS) || start;
  end = addDays(end, CALENDAR_PADDING_DAYS) || end;
  const values = new Set(enumerateDates(start, end));
  sourceDates.forEach((date) => values.add(date));
  truckDates.forEach((date) => values.add(date));
  return [...values].sort().map((value) => ({ value, ...compactDateParts(value) }));
};

export function normalizeFboTruckPlan(rawPlan, {
  savedPlan = null,
  expandCalendar = false,
  onDuplicate = null,
} = {}) {
  const plan = rawPlan || {};
  const trucks = collectUniqueTruckRows(plan, onDuplicate);
  const dates = (Array.isArray(plan.dates) ? plan.dates : [])
    .map((item) => {
      const value = toYmd(dateValue(item));
      return value ? { value, ...compactDateParts(value) } : null;
    })
    .filter(Boolean);
  const regions = (Array.isArray(plan.regions) ? plan.regions : [])
    .map((item) => ({ value: regionValue(item), label: regionLabel(item) }))
    .filter((item) => item.value);

  trucks.forEach((truck) => {
    const date = getTruckDate(truck);
    const region = getTruckRegion(truck);
    if (date && !dates.some((item) => item.value === date)) {
      dates.push({ value: date, ...compactDateParts(date) });
    }
    if (region && !regions.some((item) => item.value === region)) {
      regions.push({ value: region, label: truck.okrug_short || region });
    }
  });

  const calendarDates = expandCalendar ? buildCalendarDates(savedPlan || plan, trucks, plan) : [];
  const normalizedDates = calendarDates.length ? calendarDates : dates;
  normalizedDates.sort((a, b) => String(a.value).localeCompare(String(b.value)));
  regions.sort((a, b) => String(a.label || a.value).localeCompare(String(b.label || b.value), 'ru'));

  const cells = trucks.reduce((result, truck, index) => {
    const key = truckCellKey(getTruckDate(truck), getTruckRegion(truck));
    if (!result[key]) result[key] = [];
    result[key].push({ ...truck, id: getTruckId(truck) ?? truck.id, _idx: index });
    return result;
  }, {});

  return { dates: normalizedDates, regions, cells, trucks };
}

export const getTruckCapacity = (truck) => {
  const raw = truck?.capacity || truck?.capacity_pallets || truck?.truck_capacity || truck?.type || truck?.truck_type;
  const parsed = Number(String(raw || '').replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const getTruckPallets = (truck) => truck?.used_pallets
  ?? truck?.pallets_total
  ?? truck?.pallets
  ?? truck?.total_pallets;

export const isTruckUnderloaded = (truck) => Boolean(
  truck?.underloaded
  || truck?.underfill
  || truck?.status === 'underloaded'
  || truck?.status === 'underfill'
);

export const getTruckTypeLabel = (truck) => {
  const capacity = getTruckCapacity(truck);
  const raw = truck?.type || truck?.truck_type;
  if (capacity === 16 || capacity === 32) return `${capacity}`;
  if (raw) return String(raw);
  return capacity ? `${capacity}` : '—';
};

export const getTruckStatusMeta = (truck) => {
  if (isTruckUnderloaded(truck)) return { label: 'Недогруз', color: 'warning' };
  if (truck?.status === 'overfill') return { label: 'Перегруз', color: 'error' };
  if (truck?.status === 'full') return { label: 'Полная', color: 'success' };
  if (truck?.status_label) return { label: truck.status_label, color: 'default' };
  return { label: 'План', color: 'default' };
};
